import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Calendar, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CALENDLY_URL = 'https://calendly.com/contact-bankkimedia/30min';

const ExitIntentPopup = () => {
  const [show, setShow] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const { t } = useTranslation();

  const handleMouseLeave = useCallback((e) => {
    if (e.clientY <= 5 && !dismissed) {
      setShow(true);
    }
  }, [dismissed]);

  useEffect(() => {
    const alreadyShown = sessionStorage.getItem('exitPopupShown');
    if (alreadyShown) { setDismissed(true); return; }
    const timer = setTimeout(() => {
      document.addEventListener('mouseleave', handleMouseLeave);
    }, 5000);
    return () => { clearTimeout(timer); document.removeEventListener('mouseleave', handleMouseLeave); };
  }, [handleMouseLeave]);

  const dismiss = () => { setShow(false); setDismissed(true); sessionStorage.setItem('exitPopupShown', 'true'); };

  return (
    <AnimatePresence>
      {show && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="exit-intent-popup">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={dismiss} />
          <motion.div initial={{ scale: 0.85, opacity: 0, y: 30 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.85, opacity: 0, y: 30 }} transition={{ type: 'spring', damping: 25, stiffness: 350 }} className="relative w-full max-w-md bg-[#111] border border-white/10 rounded-2xl p-8 text-center">
            <button onClick={dismiss} className="absolute top-4 right-4 w-8 h-8 rounded-full bg-white/5 flex items-center justify-center hover:bg-white/10 transition-colors" data-testid="exit-popup-close">
              <X className="w-4 h-4 text-gray-400" />
            </button>
            <div className="w-16 h-16 mx-auto mb-5 rounded-full bg-[#c8ff00]/10 border border-[#c8ff00]/30 flex items-center justify-center">
              <Calendar className="w-8 h-8 text-[#c8ff00]" />
            </div>
            <h3 className="text-2xl md:text-3xl font-bold font-['Syne'] text-white mb-3">{t('exitPopup.title')}</h3>
            <p className="text-gray-400 text-sm leading-relaxed mb-6">{t('exitPopup.desc')}</p>
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" onClick={dismiss} data-testid="exit-popup-cta">
              <motion.span whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-flex items-center gap-2 px-8 py-4 bg-[#c8ff00] text-black font-bold rounded-full text-lg hover:bg-[#d4ff33] transition-colors">
                {t('exitPopup.cta')}
                <ArrowRight className="w-5 h-5" />
              </motion.span>
            </a>
            <p className="text-white/20 text-xs mt-4">{t('exitPopup.sub')}</p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default ExitIntentPopup;
